import { useEffect, useRef, useState } from 'react';
import type { ServiceReview } from '@/mocks/services';

interface Props {
  reviews: ServiceReview[];
  serviceName: string;
}

function StarRating({ rating, size = 'sm' }: { rating: number; size?: 'sm' | 'lg' }) {
  const cls = size === 'lg' ? 'text-xl' : 'text-sm';
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: 5 }, (_, i) => (
        <i
          key={i}
          className={`${i < rating ? 'ri-star-fill text-amber-400' : 'ri-star-line text-gray-300'} ${cls}`}
        />
      ))}
    </div>
  );
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString('tr-TR', { year: 'numeric', month: 'long', day: 'numeric' });
}

export default function ServiceReviews({ reviews, serviceName }: Props) {
  const sectionRef = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const avgRating = reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length;
  const roundedAvg = Math.round(avgRating * 10) / 10;

  return (
    <section
      ref={sectionRef}
      className="bg-white rounded-2xl p-6 md:p-8"
      aria-label={`${serviceName} müşteri yorumları`}
    >
      {/* Header */}
      <div
        className={`flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 transition-all duration-600 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
      >
        <div>
          <h2
            className="text-base md:text-xl font-extrabold text-gray-900 mb-1"
            style={{ fontFamily: 'Poppins, sans-serif' }}
          >
            Müşteri Yorumları
          </h2>
          <p className="text-gray-500 text-xs">
            {reviews.length} gerçek müşteri değerlendirmesi
          </p>
        </div>

        {/* Aggregate badge */}
        <div className="flex items-center gap-3 bg-amber-50 border border-amber-100 rounded-2xl px-4 py-3">
          <div className="text-center">
            <p className="text-3xl font-extrabold text-amber-500 leading-none">{roundedAvg.toFixed(1)}</p>
            <p className="text-xs text-gray-500 mt-0.5">/ 5.0</p>
          </div>
          <div>
            <StarRating rating={Math.round(roundedAvg)} size="lg" />
            <p className="text-xs text-gray-600 mt-1 font-semibold">{reviews.length} yorum</p>
          </div>
        </div>
      </div>

      {/* Review cards */}
      <div className="space-y-4">
        {reviews.map((review, i) => (
          <div
            key={i}
            className={`border border-gray-100 rounded-2xl p-4 md:p-5 transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
            style={{ transitionDelay: `${i * 100}ms` }}
            itemScope
            itemType="https://schema.org/Review"
          >
            <div className="flex items-start gap-3">
              {/* Avatar */}
              <div className="w-11 h-11 rounded-full overflow-hidden shrink-0 border-2 border-gray-100">
                <img
                  src={review.avatar}
                  alt={review.author}
                  className="w-full h-full object-cover object-top"
                  loading="lazy"
                />
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  <span
                    className="font-bold text-gray-900 text-sm"
                    itemProp="author"
                    itemScope
                    itemType="https://schema.org/Person"
                  >
                    <span itemProp="name">{review.author}</span>
                  </span>
                  <span className="flex items-center gap-1 text-xs text-gray-400">
                    <i className="ri-map-pin-line text-xs"></i>
                    {review.location}
                  </span>
                  <span className="ml-auto text-xs text-gray-400 hidden sm:block">
                    <time itemProp="datePublished" dateTime={review.date}>
                      {formatDate(review.date)}
                    </time>
                  </span>
                </div>

                <div
                  className="mb-2"
                  itemProp="reviewRating"
                  itemScope
                  itemType="https://schema.org/Rating"
                >
                  <meta itemProp="ratingValue" content={String(review.rating)} />
                  <meta itemProp="bestRating" content="5" />
                  <StarRating rating={review.rating} />
                </div>

                <p
                  className="text-gray-700 text-sm leading-relaxed"
                  itemProp="reviewBody"
                >
                  {review.text}
                </p>

                <span className="text-xs text-gray-400 mt-1 block sm:hidden">
                  <time dateTime={review.date}>{formatDate(review.date)}</time>
                </span>
              </div>
            </div>

            {/* Verified badge */}
            <div className="flex items-center gap-1.5 mt-3 pt-3 border-t border-gray-50">
              <span className="w-5 h-5 flex items-center justify-center bg-green-100 rounded-full">
                <i className="ri-check-line text-green-600 text-xs"></i>
              </span>
              <span className="text-xs text-gray-400 font-medium">Doğrulanmış Müşteri</span>
            </div>
          </div>
        ))}
      </div>

      {/* CTA to leave review */}
      <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-3 bg-gray-50 rounded-2xl p-4">
        <div>
          <p className="text-sm font-bold text-gray-900">Siz de deneyiminizi paylaşın</p>
          <p className="text-xs text-gray-500">Google'da yorum bırakarak bize destek olabilirsiniz</p>
        </div>
        <a
          href="https://maps.google.com/?q=Van+Polat+Tesisat"
          target="_blank"
          rel="nofollow noreferrer"
          className="flex items-center gap-2 bg-amber-400 hover:bg-amber-500 text-white font-bold text-xs px-4 py-2.5 rounded-xl transition-colors cursor-pointer whitespace-nowrap shrink-0"
        >
          <i className="ri-star-line text-sm"></i>
          Google'da Yorum Yaz
        </a>
      </div>
    </section>
  );
}
